import * as THREE from "three";
import type { Face, MaterialData, MeshData, SceneDocument, SelectionState, TextureAsset, Vec3 } from "../types";
import { deriveTopology, triangulateFace, vertexMap } from "../core/mesh";

export interface RenderMeshBundle {
  root: THREE.Group;
  surface: THREE.Mesh;
  edges: THREE.LineSegments;
  vertices: THREE.Points;
  faceIds: number[];
  edgeIds: number[];
  vertexIds: number[];
  dispose: () => void;
}

function makeCheckerTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d")!;
  const cells = 8;
  for (let y = 0; y < cells; y += 1) {
    for (let x = 0; x < cells; x += 1) {
      context.fillStyle = (x + y) % 2 ? "#101310" : "#b7ff56";
      context.fillRect(x * canvas.width / cells, y * canvas.height / cells, canvas.width / cells, canvas.height / cells);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

function loadTexture(asset: TextureAsset | undefined): THREE.Texture | undefined {
  if (!asset) return undefined;
  const texture = new THREE.TextureLoader().load(asset.dataUrl);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

function materialFor(
  material: MaterialData,
  textures: TextureAsset[],
  display: "wireframe" | "shaded" | "textured" | "checker",
  checker: THREE.Texture,
): THREE.MeshStandardMaterial {
  const map = display === "checker"
    ? checker
    : display === "textured"
      ? loadTexture(textures.find((asset) => asset.id === material.baseColorTextureId))
      : undefined;
  return new THREE.MeshStandardMaterial({
    name: material.name,
    color: display === "checker" ? "#ffffff" : material.color,
    ...(map ? { map } : {}),
    roughness: material.roughness,
    metalness: material.metallic,
    side: THREE.DoubleSide,
    wireframe: display === "wireframe",
    flatShading: true,
  });
}

function selectedFaceGeometry(mesh: MeshData, selected: Set<number>): THREE.BufferGeometry | null {
  const positions: number[] = [];
  const vertices = vertexMap(mesh);
  for (const face of mesh.faces) {
    if (!selected.has(face.id)) continue;
    for (const triangle of triangulateFace(face)) {
      for (const corner of triangle) positions.push(...vertices.get(face.vertices[corner])!.position);
    }
  }
  if (!positions.length) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  return geometry;
}

export function createRenderMesh(
  mesh: MeshData,
  scene: SceneDocument,
  selection: SelectionState,
  display: "wireframe" | "shaded" | "textured" | "checker",
): RenderMeshBundle {
  const root = new THREE.Group();
  root.name = mesh.name;
  root.userData.objectId = mesh.id;
  root.position.set(...mesh.transform.position);
  root.rotation.set(...mesh.transform.rotation);
  root.scale.set(...mesh.transform.scale);
  root.visible = mesh.visible;

  const positions: number[] = [];
  const uvs: number[] = [];
  const faceIds: number[] = [];
  const materialIds = [...new Set(mesh.faces.map((face) => face.materialId))];
  const checker = makeCheckerTexture();
  const materials = materialIds.map((id) => materialFor(
    scene.materials.find((material) => material.id === id) ?? scene.materials[0],
    scene.textures,
    display,
    checker,
  ));
  const verticesById = vertexMap(mesh);
  const groups: Array<{ start: number; count: number; materialIndex: number }> = [];
  let triangleVertex = 0;
  for (const face of mesh.faces) {
    const materialIndex = Math.max(0, materialIds.indexOf(face.materialId));
    for (const triangle of triangulateFace(face)) {
      for (const corner of triangle) {
        positions.push(...verticesById.get(face.vertices[corner])!.position);
        uvs.push(...(face.uvs[corner] ?? [0, 0]));
      }
      faceIds.push(face.id);
      groups.push({ start: triangleVertex, count: 3, materialIndex });
      triangleVertex += 3;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  groups.forEach((group) => geometry.addGroup(group.start, group.count, group.materialIndex));
  const surface = new THREE.Mesh(geometry, materials);
  surface.name = `${mesh.name}:surface`;
  surface.userData.objectId = mesh.id;
  surface.userData.faceIds = faceIds;
  surface.renderOrder = 0;
  root.add(surface);

  const topology = deriveTopology(mesh);
  const edgePositions: number[] = [];
  const edgeIds: number[] = [];
  for (const edge of topology.edges) {
    edgePositions.push(...verticesById.get(edge.a)!.position, ...verticesById.get(edge.b)!.position);
    edgeIds.push(edge.id);
  }
  const edgeGeometry = new THREE.BufferGeometry();
  edgeGeometry.setAttribute("position", new THREE.Float32BufferAttribute(edgePositions, 3));
  const edges = new THREE.LineSegments(edgeGeometry, new THREE.LineBasicMaterial({
    color: display === "wireframe" ? "#d4ddd6" : "#26302a",
    transparent: true,
    opacity: display === "wireframe" ? 0.92 : 0.72,
    depthTest: true,
  }));
  edges.name = `${mesh.name}:edges`;
  edges.userData.objectId = mesh.id;
  edges.userData.edgeIds = edgeIds;
  edges.renderOrder = 2;
  root.add(edges);

  const pointGeometry = new THREE.BufferGeometry();
  pointGeometry.setAttribute("position", new THREE.Float32BufferAttribute(mesh.vertices.flatMap((vertex) => vertex.position), 3));
  const vertices = new THREE.Points(pointGeometry, new THREE.PointsMaterial({ color: "#d8e2dc", size: 5, sizeAttenuation: false, depthTest: true }));
  vertices.name = `${mesh.name}:vertices`;
  vertices.userData.objectId = mesh.id;
  vertices.userData.vertexIds = mesh.vertices.map((vertex) => vertex.id);
  vertices.renderOrder = 3;
  vertices.visible = selection.mode === "vertex" && selection.objectIds.includes(mesh.id);
  root.add(vertices);

  const isActive = selection.objectIds.includes(mesh.id);
  if (isActive && selection.mode === "face" && selection.componentIds.length) {
    const selectedGeometry = selectedFaceGeometry(mesh, new Set(selection.componentIds));
    if (selectedGeometry) {
      const overlay = new THREE.Mesh(selectedGeometry, new THREE.MeshBasicMaterial({ color: "#ffb020", transparent: true, opacity: 0.42, depthTest: true, polygonOffset: true, polygonOffsetFactor: -2, side: THREE.DoubleSide }));
      overlay.name = `${mesh.name}:face-selection`;
      overlay.renderOrder = 4;
      root.add(overlay);
    }
  }
  if (isActive && selection.mode === "edge" && selection.componentIds.length) {
    const selected = new Set(selection.componentIds);
    const selectedPositions: number[] = [];
    topology.edges.filter((edge) => selected.has(edge.id)).forEach((edge) => selectedPositions.push(...verticesById.get(edge.a)!.position, ...verticesById.get(edge.b)!.position));
    const selectedGeometry = new THREE.BufferGeometry();
    selectedGeometry.setAttribute("position", new THREE.Float32BufferAttribute(selectedPositions, 3));
    const overlay = new THREE.LineSegments(selectedGeometry, new THREE.LineBasicMaterial({ color: "#ffb020", depthTest: false }));
    overlay.name = `${mesh.name}:edge-selection`;
    overlay.renderOrder = 5;
    root.add(overlay);
  }
  if (isActive && selection.mode === "vertex" && selection.componentIds.length) {
    const selected = new Set(selection.componentIds);
    const selectedPositions = mesh.vertices.filter((vertex) => selected.has(vertex.id)).flatMap((vertex) => vertex.position);
    const selectedGeometry = new THREE.BufferGeometry();
    selectedGeometry.setAttribute("position", new THREE.Float32BufferAttribute(selectedPositions, 3));
    const overlay = new THREE.Points(selectedGeometry, new THREE.PointsMaterial({ color: "#ffb020", size: 9, sizeAttenuation: false, depthTest: false }));
    overlay.name = `${mesh.name}:vertex-selection`;
    overlay.renderOrder = 5;
    root.add(overlay);
  }

  if (isActive && selection.mode === "object") {
    const box = new THREE.BoxHelper(surface, 0xffb020);
    box.name = `${mesh.name}:object-selection`;
    box.renderOrder = 5;
    root.add(box);
  }

  return {
    root,
    surface,
    edges,
    vertices,
    faceIds,
    edgeIds,
    vertexIds: mesh.vertices.map((vertex) => vertex.id),
    dispose: () => {
      root.traverse((child) => {
        const renderable = child as THREE.Mesh;
        renderable.geometry?.dispose?.();
        const childMaterials = Array.isArray(renderable.material) ? renderable.material : renderable.material ? [renderable.material] : [];
        childMaterials.forEach((material) => material.dispose());
      });
      checker.dispose();
      materials.forEach((material) => material.map?.dispose());
    },
  };
}

export function selectedComponentCenter(mesh: MeshData, selection: SelectionState): Vec3 {
  let ids: number[] = [];
  if (selection.mode === "vertex") ids = selection.componentIds;
  if (selection.mode === "face") {
    const selected = new Set(selection.componentIds);
    ids = [...new Set(mesh.faces.filter((face) => selected.has(face.id)).flatMap((face) => face.vertices))];
  }
  if (selection.mode === "edge") {
    const topology = deriveTopology(mesh);
    ids = [...new Set(selection.componentIds.flatMap((id) => {
      const edge = topology.edgeById.get(id);
      return edge ? [edge.a, edge.b] : [];
    }))];
  }
  const points = mesh.vertices.filter((vertex) => ids.includes(vertex.id)).map((vertex) => vertex.position);
  if (!points.length) return mesh.transform.pivot;
  const sum = points.reduce((acc, point) => [acc[0] + point[0], acc[1] + point[1], acc[2] + point[2]] as Vec3, [0, 0, 0]);
  return [sum[0] / points.length, sum[1] / points.length, sum[2] / points.length];
}
